import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { BookingResponse, BookingResponseData } from '../../../../services/payment-report/response/BookingResponse';
import { useAuth } from '../../../../auth/context/AuthProvider';

interface PaymentTableReportProps {
    data: BookingResponse;
    filter?: {
        property_id?: string;
        periode?: {
            start: string;
            end: string;
        };
    };
}

const PaymentTableReport: React.FC<PaymentTableReportProps> = ({ data, filter }) => {
    const { user } = useAuth();

    const columnsReservations = [
        { label: "Date", key: "date" },
        { label: "Added", key: "added" },
        { label: "Room Type", key: "room_type" },
        { label: "Room Number", key: "room_number" },
        { label: "User", key: "user" },
        { label: "Source", key: "source" },
        { label: "Reservation Number", key: "reservation_number" },
        { label: "Check-In", key: "check_in" },
        { label: "Guest Name", key: "guest_name" },
        { label: "Payment Type", key: "card_type" },
        { label: "Surcharge", key: "surcharge" },
        { label: "Amount", key: "amount" }
    ];

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Sales Report", 14, 10);

        const tableData = data.data.map(row => columnsReservations.map(col => {
            if (col.key === 'rates' || col.key === 'room_only') {
                return formatPrice(Number(row[col.key as keyof BookingResponseData]));
            }
            return row[col.key as keyof BookingResponseData] || '-';
        }));

        autoTable(doc, {
            head: [columnsReservations.map(col => col.label)],
            body: tableData,
        });

        doc.save("PaymentReport.pdf");
    };

    // Export to Excel
    const exportToExcel = () => {
        const propertyId = filter?.property_id || user?.active_property_id;
        
        if (!propertyId || !filter?.periode?.start || !filter?.periode?.end) {
            console.error('Missing required filter data for export');
            return;
        }

        const baseUrl = import.meta.env.VITE_API_URL || '';
        const exportUrl = `${baseUrl}/v4/fos/payment-report/export?filter[property_id]=${propertyId}&filter[start]=${filter.periode.start}&filter[end]=${filter.periode.end}`;
        
        console.log('Navigating to export URL:', exportUrl);
        window.location.href = exportUrl;
    };    
    
    const formatCellValue = (row: BookingResponseData, key: string) => {
        const value = row[key as keyof BookingResponseData];

        if (key === 'amount') {
            return formatPrice(parseFloat(value as string) || 0);
        }

        if (key === 'surcharge') {
            return `${parseFloat(value as string) || 0} %`;
        }

        return value || '-';
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button 
                    variant="success" 
                    onClick={exportToExcel}
                    disabled={!(filter?.property_id || user?.active_property_id) || !filter?.periode?.start || !filter?.periode?.end}
                >
                    Export to Excel
                </Button>
            </div>            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            {columnsReservations.map((col, index) => (
                                <th key={index}>{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.data.length > 0 ? (
                            [...data.data]
                                .sort((a, b) => new Date(a.check_in).getTime() - new Date(b.check_in).getTime())
                                .map((row, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {columnsReservations.map((col, colIndex) => (
                                            <td key={colIndex}>
                                                {col.key === 'surcharge' || col.key === 'amount'
                                                    ? formatCellValue(row, col.key)
                                                    : row[col.key as keyof BookingResponseData] || '-'}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                        ) : (
                            <tr>
                                <td colSpan={columnsReservations.length} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={10}><strong>Total</strong></td>
                            <td><strong>{`${parseFloat(String(data.total.total_surcharge)) || 0} %`}</strong></td>
                            <td><strong>{formatPrice(parseFloat(String(data.total.total_amount)) || 0)}</strong></td>

                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default PaymentTableReport;